import { gql } from "lib/apolloClient";
import { deadlineFragments } from "./ProjectFragments";
import { attachmentFragment, userNameFragment } from "modules/CommonFragments";
import { projectAttachmentFragment } from "UnitPlans/modules/UnitPlanFragments";

export const deleteProjectDeadlineMutation = gql`
  mutation deleteProjectDeadline($input: DeleteProjectGroupDeadlineInput!) {
    documentation {
      deleteProjectGroupDeadline(input: $input)
    }
  }
`;

export const updateProjectDeadlineMutation = gql`
  mutation updateProjectDeadline(
    $input: UpdateProjectGroupDeadlineInput!
    $fetchStatusWiseProjects: Boolean!
    $statusWiseProjectFilters: StatusWiseProjectFilters
  ) {
    documentation {
      updateProjectGroupDeadline(input: $input) {
        ...projectDeadline
      }
    }
  }
  ${deadlineFragments.deadlineFragmentWithStatusCount}
`;

export const createProjectDeadlineMutation = gql`
  mutation createProjectDeadline(
    $input: CreateProjectGroupDeadlineInput!
    $fetchStatusWiseProjects: Boolean!
    $statusWiseProjectFilters: StatusWiseProjectFilters
  ) {
    documentation {
      createProjectGroupDeadline(input: $input) {
        ...projectDeadline
      }
    }
  }
  ${deadlineFragments.deadlineFragmentWithStatusCount}
`;

export const createProjectTemplateCopyMutation = gql`
  mutation createProjectTemplateCopy($input: CreateProjectTemplateCopyInput!) {
    documentation {
      createProjectTemplateCopy(input: $input) {
        ...attachmentItem
        presetAttachment {
          ...attachmentItem
        }
      }
    }
  }
  ${attachmentFragment.attachment}
`;

export const updateProjectFieldMutation = gql`
  mutation updateProjectField($input: UpdateProjectInput!) {
    documentation {
      updateProject(input: $input) {
        id
      }
    }
  }
`;

export const markProjectDeadlinesCompleteMutation = gql`
  mutation markProjectDeadlinesComplete(
    $projectId: ID!
    $addedArray: [ID!]
    $removedArray: [ID!]
  ) {
    documentation {
      markProjectDeadlinesComplete(
        input: {
          projectId: $projectId
          deadlines: { added: $addedArray, removed: $removedArray }
        }
      )
    }
  }
`;

export const updateProjectMutation = gql`
  mutation updateProject(
    $projectId: ID!
    $status: String
    $reportStatus: String
    $type: PROJECT_TYPE_ENUM
    $statusMessage: String
    $isCompleted: Boolean
    $fields: [UpdatePlannerFieldInput!]
    $isCasExternalPortingDone: Boolean
  ) {
    documentation {
      updateProject(
        input: {
          status: $status
          id: $projectId
          reportStatus: $reportStatus
          type: $type
          statusMessage: $statusMessage
          isCompleted: $isCompleted
          fields: $fields
          isCasExternalPortingDone: $isCasExternalPortingDone
        }
      ) {
        id
        status
        reportStatus
        type
        isCompleted
        markedCompletedBy {
          ...userNameFields
        }
        markedCompletedAt
        fields {
          id
          uid
          value
        }
      }
    }
  }
  ${userNameFragment}
`;

export const updateProjectWithoutResponseFieldsMutation = gql`
  mutation updateProject(
    $projectId: ID!
    $status: String
    $reportStatus: String
    $type: PROJECT_TYPE_ENUM
    $statusMessage: String
    $isCompleted: Boolean
  ) {
    documentation {
      updateProject(
        input: {
          status: $status
          id: $projectId
          reportStatus: $reportStatus
          type: $type
          statusMessage: $statusMessage
          isCompleted: $isCompleted
        }
      ) {
        id
      }
    }
  }
`;

export const createProjectMutation = gql`
  mutation createProject($input: CreateProjectInput!) {
    documentation {
      createProject(input: $input) {
        id
      }
    }
  }
`;

export const bulkCreateProjectMutation = gql`
  mutation bulkCreateProjects($input: CreateProjectInput!) {
    documentation {
      bulkCreateProjects(input: $input) {
        projects {
          id
          students {
            id
          }
        }
      }
    }
  }
`;

export const editProjectMutation = gql`
  mutation editProject(
    $projectId: ID!
    $addedSupervisors: [ID!]
    $removedSupervisors: [ID!]
    $removedStudents: [ID!]
    $addedStudents: [ID!]
    $removedStudentCollaborators: [ID!]
    $addedStudentCollaborators: [ID!]
  ) {
    documentation {
      assignProjectParticipants(
        input: {
          projectId: $projectId
          supervisor: { added: $addedSupervisors, removed: $removedSupervisors }
          student: { added: $addedStudents, removed: $removedStudents }
          studentCollaborators: {
            added: $addedStudentCollaborators
            removed: $removedStudentCollaborators
          }
        }
      )
    }
  }
`;

export const removeProjectsMutation = gql`
  mutation removeProjects($projectIds: [ID!]!) {
    documentation {
      removeProjects(input: { projectIds: $projectIds })
    }
  }
`;

export const restoreProjectsMutation = gql`
  mutation restoreProjects($projectIds: [ID!]!) {
    documentation {
      restoreProjects(input: { projectIds: $projectIds }) {
        isSuccess
      }
    }
  }
`;

export const createProjectGroupGuidingQuestionMutation = gql`
  mutation createProjectGroupGuidingQuestion(
    $question: String!
    $projectGroupId: ID!
    $grades: [ID!]
  ) {
    documentation {
      createProjectGroupGuidingQuestion(
        input: {
          question: $question
          projectGroupId: $projectGroupId
          grades: $grades
        }
      ) {
        id
      }
    }
  }
`;

export const updateProjectGroupGuidingQuestionMutaion = gql`
  mutation updateProjectGroupGuidingQuestion(
    $question: String!
    $id: ID!
    $gradeAdded: [ID!]
    $gradeRemoved: [ID!]
  ) {
    documentation {
      updateProjectGroupGuidingQuestion(
        input: {
          question: $question
          id: $id
          grades: { added: $gradeAdded, removed: $gradeRemoved }
        }
      )
    }
  }
`;

export const deleteProjectGroupGuidingQuestionMutation = gql`
  mutation deleteProjectGroupGuidingQuestion($id: ID!) {
    documentation {
      deleteProjectGroupGuidingQuestion(input: { id: $id })
    }
  }
`;

export const reorderProjectGroupGuidingQuestionMutation = gql`
  mutation reorderProjectGroupGuidingQuestion($id: ID!, $putAfterItemId: ID) {
    documentation {
      reorderProjectGroupGuidingQuestion(
        input: { id: $id, putAfterItemId: $putAfterItemId }
      )
    }
  }
`;

export const updateStudentProjectPortfolioMutation = gql`
  mutation updateStudentProjectPortfolio(
    $input: UpdateStudentProjectPortfolioInput!
  ) {
    documentation {
      updateStudentProjectPortfolio(input: $input) {
        id
      }
    }
  }
`;

export const updateStudentPortfolioStatusMutation = gql`
  mutation updateStudentPortfolioStatus(
    $id: ID!
    $status: STUDENT_PROJECT_PORTFOLIO_STATUS_ENUM
    $isComplete: Boolean
  ) {
    documentation {
      updateStudentProjectPortfolio(
        input: { id: $id, status: $status, isComplete: $isComplete }
      ) {
        id
      }
    }
  }
`;

export const updateAchievedPlannerElementMutation = gql`
  mutation updateAchievedPlannerElement(
    $input: UpdateAchievedPlannerElementsInput!
  ) {
    planner {
      updateAchievedPlannerElements(input: $input)
    }
  }
`;

export const updateProjectFieldValuesForStudentAndProjectGroupsMutation = gql`
  mutation updateProjectFieldValuesForStudentAndProjectGroupsMutation(
    $projectGroupTypes: [String!]!
    $studentId: ID!
    $fieldUpdateData: [fieldUpdateDataInput!]!
  ) {
    documentation {
      updateProjectFieldValuesForStudentAndProjectGroups(
        input: {
          projectGroupTypes: $projectGroupTypes
          studentId: $studentId
          fieldUpdateData: $fieldUpdateData
        }
      ) {
        isSuccess
        warning
      }
    }
  }
`;

export const generateProjectFileUrlMutation = gql`
  mutation generateProjectFileUrl(
    $projectIds: [ID!]
    $attachmentTypesInput: [ProjectAttachmentTypeInput!]
    $isLongRunningTask: Boolean
    $academicYearIds: [ID!]
  ) {
    platform {
      generateProjectFileUrl(
        projectIds: $projectIds
        attachmentTypesInput: $attachmentTypesInput
        isLongRunningTask: $isLongRunningTask
        academicYearIds: $academicYearIds
      ) {
        url
        longRunningTaskCreated
        isSuccess
      }
    }
  }
`;

export const generateProjectFileFolderUrlMutation = gql`
  mutation generateProjectFileFolderUrl(
    $projectIds: [ID!]
    $fileFolderTypesInput: [ProjectFileFolderTypeInput!]
    $fileFolderConfig: ProjectFileFolderConfigInput
    $courseIds: [ID!]
    $isLongRunningTask: Boolean
    $academicYearIds: [ID!]
  ) {
    platform {
      generateProjectFileFolderUrl(
        projectIds: $projectIds
        fileFolderTypesInput: $fileFolderTypesInput
        fileFolderConfig: $fileFolderConfig
        courseIds: $courseIds
        isLongRunningTask: $isLongRunningTask
        academicYearIds: $academicYearIds
      ) {
        name
        url
        longRunningTaskCreated
        isSuccess
      }
    }
  }
`;

export const generatePortfolioFileFolderUrlMutation = gql`
  mutation generatePortfolioFileFolderUrl(
    $portfolioIds: [ID!]
    $fileFolderTypesInput: [ProjectFileFolderTypeInput!]
    $fileFolderConfig: PortfolioFileFolderConfigInput
    $isLongRunningTask: Boolean
    $academicYearIds: [ID!]
  ) {
    platform {
      generatePortfolioFileFolderUrl(
        portfolioIds: $portfolioIds
        fileFolderTypesInput: $fileFolderTypesInput
        fileFolderConfig: $fileFolderConfig
        isLongRunningTask: $isLongRunningTask
        academicYearIds: $academicYearIds
      ) {
        name
        url
        longRunningTaskCreated
        isSuccess
      }
    }
  }
`;

export const generateFileFolderUrlByStudentProjectGroupMutation = gql`
  mutation generateFileFolderUrlByStudentProjectGroup(
    $studentIds: [ID!]!
    $projectGroupId: ID!
    $fileFolderTypesInput: [ProjectFileFolderTypeInput!]
    $fileFolderConfig: StudentFileFolderConfigInput
    $courseIds: [ID!]
    $isLongRunningTask: Boolean
  ) {
    platform {
      generateStudentProjectGroupUrl(
        studentIds: $studentIds
        fileFolderTypesInput: $fileFolderTypesInput
        fileFolderConfig: $fileFolderConfig
        courseIds: $courseIds
        projectGroupId: $projectGroupId
        isLongRunningTask: $isLongRunningTask
      ) {
        name
        url
        longRunningTaskCreated
        isSuccess
      }
    }
  }
`;

export const updateProjectGroupMutation = gql`
  mutation updateProjectGroup($input: ProjectGroupUpdateInput!) {
    documentation {
      updateProjectGroup(input: $input) {
        id
      }
    }
  }
`;

export const bulkUpdateEntityLockingStatusMutation = gql`
  mutation bulkUpdateEntityLockingStatus(
    $input: BulkUpdateEntityLockingStatusInput!
  ) {
    platform {
      bulkUpdateEntityLockingStatus(input: $input) {
        isSuccess
        warning
      }
    }
  }
`;

export const createAttachmentMutationForProjects = gql`
  mutation createAttachment($input: [AttachmentInput!]) {
    platform {
      createAttachment(input: $input) {
        ...attachmentItemV2
      }
    }
  }
  ${projectAttachmentFragment.attachment}
`;

export const removeLosFromPostsMutation = gql`
  mutation bulkUpdatePostFields($input: BulkUpdatePostFieldInput!) {
    documentation {
      bulkUpdatePostFields(input: $input) {
        isSuccess
        warning
      }
    }
  }
`;

export const updateElementRemarkMutationMutation = gql`
  mutation updateElementRemarkMutation($input: [IBPYPElementRemarkInput!]) {
    planner {
      createElementRemarks(input: $input) {
        warning
        response {
          id
          message
          ibPYPElementType
          ibPYPElementId
        }
      }
    }
  }
`;

export const bulkUpdateProjectEntityMutation = gql`
  mutation bulkUpdateProjectEntity($input: BulkUpdateProjectEntityInput!) {
    documentation {
      bulkUpdateProjectEntity(input: $input) {
        isSuccess
        warning
      }
    }
  }
`;
